import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { ToggleButton } from '@/components/UI';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import ArrowIcon from '@/public/icons/arrow.svg';
import { descriptionMotion, dropdownMotion } from '../../../animation';
import { useCookiesContext } from '../../../context';
import { useUpdateCookiesToggles } from '../../../hooks';
import styles from './CookiesItem.module.scss';

// Props Types
interface CookiesItemProps {
	item: {
		title: string;
		description: string;
	};
	name: string;
	expanded: string | boolean;
	setExpanded: React.Dispatch<React.SetStateAction<string | boolean>>;
}

export const CookiesItem: React.FC<CookiesItemProps> = ({
	item,
	name,
	expanded,
	setExpanded,
}) => {
	// Context
	const { cookies } = useCookiesContext();
	const { t } = useTranslationWithLocale('common');

	// Helpers
	const isOpen = name === expanded;
	const { title, description } = item;
	// - hooks
	const onChangeToggle = useUpdateCookiesToggles();

	// Actions
	const onClick = () => {
		setExpanded(isOpen ? false : name);
	};

	return (
		<div className={styles['item']}>
			<div className={styles['item__header']}>
				<motion.button
					type='button'
					className={styles['item__button']}
					onClick={onClick}
					whileFocus={{ color: '#15B993' }}
					whileHover={{ color: '#15B993' }}
					animate={{ color: isOpen ? '#15B993' : '#130F07' }}
					transition={{ duration: 0.25 }}
				>
					<motion.span
						className={styles['item__button-icon']}
						animate={{ rotate: isOpen ? 90 : 0 }}
						transition={{ duration: 0.25 }}
					>
						<ArrowIcon className={styles['item__button-svg']} />
					</motion.span>
					<span className={styles['item__title']}>{t(title)}</span>
				</motion.button>
				<ToggleButton
					name={name}
					onChange={onChangeToggle}
					disabled={name === 'essential'}
					isChecked={cookies.includes(name)}
					classes={{
						toggle: styles['item__toggle'],
					}}
				/>
			</div>
			<AnimatePresence initial={false}>
				{isOpen ? (
					<motion.div
						className={styles['item__content']}
						// Animation
						{...dropdownMotion}
					>
						<motion.div
							className={styles['item__wrapper']}
							// Animation
							{...descriptionMotion}
						>
							<p className={styles['item__text']}>{t(description)}</p>
						</motion.div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</div>
	);
};
